import { redactCredentialText } from "@cleo/shared/credentialRedaction";
import * as Schema from "effect/Schema";
import * as HttpClientError from "effect/unstable/http/HttpClientError";

/**
 * What an error keeps of the failure it wraps: its tag, message, HTTP status,
 * method and URL without query string, trace ID, and the same summary of its
 * own cause. Never the failed request itself: an HTTP client error holds the
 * request's headers and body, which carry the Clerk bearer, the desktop or
 * environment bearer, a DPoP access token and proof, or a credential being
 * exchanged, and any caller that logs or inspects the error would expose them
 * (W-b8299199).
 */
export interface ErrorCauseSummary {
  readonly _tag?: string;
  readonly message: string;
  readonly status?: number;
  readonly method?: string;
  readonly url?: string;
  readonly traceId?: string;
  readonly cause?: ErrorCauseSummary;
}

export const ErrorCauseSummary: Schema.Codec<ErrorCauseSummary> = Schema.Struct({
  _tag: Schema.optionalKey(Schema.String),
  message: Schema.String,
  status: Schema.optionalKey(Schema.Number),
  method: Schema.optionalKey(Schema.String),
  url: Schema.optionalKey(Schema.String),
  traceId: Schema.optionalKey(Schema.String),
  cause: Schema.optionalKey(
    Schema.suspend((): Schema.Codec<ErrorCauseSummary> => ErrorCauseSummary),
  ),
});

const MAX_CAUSE_SUMMARY_DEPTH = 4;
const SAFE_CAUSE_TAG = /^[A-Za-z][A-Za-z0-9]{0,63}$/;
const SAFE_TRACE_ID = /^[A-Za-z0-9._:-]{1,128}$/;
const UNKNOWN_CAUSE_MESSAGE = "The failure had no error message.";

function urlWithoutQuery(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split(/[?#]/, 1)[0] ?? "";
  }
}

function replaceUrl(text: string, rawUrl: string, url: string): string {
  return rawUrl.length > 0 && rawUrl !== url ? text.split(rawUrl).join(url) : text;
}

function readCauseField(value: object, key: string): unknown {
  try {
    return (value as Record<string, unknown>)[key];
  } catch {
    return undefined;
  }
}

function summarizeCause(cause: unknown, depth: number): ErrorCauseSummary {
  const nestedSummary = (nested: unknown) =>
    nested !== undefined && depth < MAX_CAUSE_SUMMARY_DEPTH
      ? { cause: summarizeCause(nested, depth + 1) }
      : {};
  if (HttpClientError.isHttpClientError(cause)) {
    const request = cause.request;
    const url = urlWithoutQuery(request.url);
    const response = cause.response;
    return {
      _tag: cause._tag,
      message: redactCredentialText(replaceUrl(cause.message, request.url, url)),
      ...(response ? { status: response.status } : {}),
      method: request.method,
      url,
      ...nestedSummary(cause.reason.cause),
    };
  }
  if (typeof cause !== "object" || cause === null) {
    return { message: UNKNOWN_CAUSE_MESSAGE };
  }
  const tag = readCauseField(cause, "_tag") ?? (cause instanceof Error ? cause.name : undefined);
  const message = readCauseField(cause, "message");
  const status = readCauseField(cause, "status");
  const method = readCauseField(cause, "method");
  const traceId = readCauseField(cause, "traceId");
  const rawUrl = readCauseField(cause, "url");
  const url = typeof rawUrl === "string" ? urlWithoutQuery(rawUrl) : undefined;
  return {
    ...(typeof tag === "string" && SAFE_CAUSE_TAG.test(tag) ? { _tag: tag } : {}),
    message:
      typeof message === "string" && message.length > 0
        ? redactCredentialText(
            typeof rawUrl === "string" && url !== undefined
              ? replaceUrl(message, rawUrl, url)
              : message,
          )
        : UNKNOWN_CAUSE_MESSAGE,
    ...(typeof status === "number" && Number.isInteger(status) ? { status } : {}),
    ...(typeof method === "string" && SAFE_CAUSE_TAG.test(method) ? { method } : {}),
    ...(url !== undefined ? { url } : {}),
    ...(typeof traceId === "string" && SAFE_TRACE_ID.test(traceId) ? { traceId } : {}),
    ...nestedSummary(readCauseField(cause, "cause")),
  };
}

/**
 * Summarizes a failure for an error's `cause`. Errors that wrap a request
 * failure (managed relay errors, environment request errors, Cleo Connect link
 * errors) keep this instead of the failure itself.
 */
export function summarizeErrorCause(cause: unknown): ErrorCauseSummary {
  return summarizeCause(cause, 0);
}
